import axios from 'axios';
import cookieManager from './cookieManager';

// Tracking state to prevent duplicate tracking during single page view
let lastTrackedPath = null;
let trackingInProgress = false;

/**
 * Check if analytics tracking is allowed based on user's cookie preferences
 * @returns {boolean} Whether analytics tracking is allowed
 */
const isAnalyticsAllowed = () => {
  return cookieManager.isCookieAccepted('analytics');
};

/**
 * Track a page view from the frontend
 * This is useful for SPA navigation that doesn't trigger server page loads
 * 
 * @param {string} path - The current path being viewed
 * @param {string|null} userId - User ID if available
 * @param {boolean} force - Whether to force tracking even if path hasn't changed
 */
export const trackPageView = async (path, userId = null, force = false) => {
  try {
    if (path.startsWith('/u/')) {
      return;
    }

    // Check if analytics tracking is allowed
    if (!isAnalyticsAllowed()) {
      console.log('[Analytics] Tracking disabled due to cookie preferences');
      return;
    }
    
    // Don't track the same path twice in quick succession
    if (!force && (path === lastTrackedPath || trackingInProgress)) {
      return;
    }
    
    // Update tracking state
    trackingInProgress = true;
    lastTrackedPath = path;
    
    console.log(`[Analytics] Tracking client-side page view: ${path}`);
    
    // Additional metadata to help with analytics
    const metadata = {
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      referrer: document.referrer || '',
      language: navigator.language || ''
    };
    
    await axios.post('/api/analytics/track', {
      path,
      userId,
      metadata
    });
    
    console.log(`[Analytics] Successfully tracked page view: ${path}`);
  } catch (error) {
    // Log the error but don't affect user experience
    console.error('[Analytics] Error tracking page view:', error.message || error);
  } finally {
    // Reset tracking state after a short delay
    setTimeout(() => {
      trackingInProgress = false;
    }, 500);
  }
};

/**
 * Track a custom event
 * 
 * @param {string} eventName - Name of the event
 * @param {Object} eventData - Additional data about the event
 * @param {string|null} userId - User ID if available
 */
export const trackEvent = async (eventName, eventData = {}, userId = null) => {
  try {
    // Check if analytics tracking is allowed
    if (!isAnalyticsAllowed()) {
      console.log('[Analytics] Event tracking disabled due to cookie preferences');
      return;
    }
    
    console.log(`[Analytics] Tracking custom event: ${eventName}`);
    
    await axios.post('/api/analytics/event', {
      eventName,
      eventData,
      userId,
      timestamp: new Date().toISOString()
    });
    
    console.log(`[Analytics] Successfully tracked event: ${eventName}`);
  } catch (error) {
    console.error('[Analytics] Error tracking event:', error.message || error);
  }
};

/**
 * Hook to track page view on component mount or route change
 * This can be used in useEffect to track page views
 * 
 * @param {string} path - The current path being viewed
 * @param {string|null} userId - User ID if available
 */
export const usePageViewTracking = (path, userId = null) => {
  // Track the page view only if analytics is allowed
  if (isAnalyticsAllowed()) {
    trackPageView(path, userId).catch(err => 
      console.error('[Analytics] Error in usePageViewTracking hook:', err)
    );
  }
};

const analytics = {
  trackPageView,
  trackEvent,
  usePageViewTracking,
  isAnalyticsAllowed
};
export default analytics; 